import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { Helmet } from 'react-helmet';
import { navigate, RouteComponentProps } from '@reach/router';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { ITasks, IUsers, RootState } from 'store/store';
import { StringOrNull, TimeType, UserType } from 'store/entity/types';
import * as taskManipulate from 'store/tasks/taskManipulate';
import { serverToStore } from 'store/tasks/taskManipulate';
import { SpaceBetween } from 'utils/style/extendable';
import Input from 'components/Input';
import notify from 'components/Notify';
import Button from 'components/Button';
import messages from './Messages';
import { display } from 'components/ErrorView/ErrorDialog';
import Collapsible from 'components/Collapsible';
import LogoLoading from 'components/LogoLoading';
import TaskReminder from '../TasksManagement/TaskReminder';
import TemplateFields from 'components/TemplateFields';
import TemplateReport from 'components/TemplateReport';
import Select, { IOption } from 'components/Select/Select';
import TemplateNotifications from './TemplateNotifications';
import { setClipboard } from '../TasksManagement/TaskUtils';
import Icon from '../../../components/Icon/Icon';
import {
  IPrintTemplatePartObject,
  ITaskPrintTemplateObject,
} from 'store/entity/IPrintTemplatePart';
import { appDispatch } from 'store/store';
import { ICompany, ICompanyTemplate } from 'store/companies/companiesTypes';
import {
  IAssignmentView,
  IField,
  IFieldNotification,
  IReminderInfo,
  ITask,
  ITaskProgressType,
  ITemplateReportOptions,
  PrintTaskParts,
} from 'store/tasks/tasksType';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  addTemplate,
  addTemplateFn,
  applyTemplateToPastTasks,
  applyTemplateToPastTasksFn,
  updateTemplate,
  updateTemplateFn,
} from 'store/companies/companiesSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  isAdvanceAdmin,
  isCompanyOwner,
  isCompanySupport,
} from 'store/companies/companiesUtils';
import { moveItemInArray } from '../../../utils/happyFunctions';
import i18n, { languageDirection } from '../../../i18n';

interface IStateToProps {
  users: IUsers;
  tasks: ITasks;
  company: ICompany;
  template?: ITask;
  thisUser: IThisUserState;
}

interface IDispatchToProps {
  addTemplate: (
    companyTemplate: ICompanyTemplate
  ) => ReturnType<typeof addTemplateFn>;
  updateTemplate: (
    companyTemplate: ICompanyTemplate
  ) => ReturnType<typeof updateTemplateFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  applyTemplateToPastTasks: (
    templateId: number
  ) => ReturnType<typeof applyTemplateToPastTasksFn>;
}

interface IOwnProps {
  companyId?: string;
  templateId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  saving: boolean;
  loading: boolean;
  template: ITask;
  subTemplates: IOption[];
  applyTemplate: boolean;
  printTemplateKeys?: string;
  printHeaderValues: PrintOption[];
  printBodyValues: PrintOption[];
  printSubtasksValues: PrintOption[];
  suggestOnDocumentManager: boolean;
  compulsoryFields: string[];
}

const initialTemplate: Partial<ITask> = {
  type: 'atOnce',
  fields: [],
  taskName: '',
  assignee: {},
  followers: {},
  chatTitle: undefined,
  haveDueDate: false,
  dateAndTime: false,
  haveSubTask: true,
  dueDateTitle: undefined,
  haveVerifier: false,
  haveFollower: false,
  haveAssignee: true,
  assigneeTitle: undefined,
  haveStartDate: false,
  subTasksTitle: undefined,
  reminderInfos: [],
  // haveVerifyDate: false,
  followersTitle: undefined,
  startDateTitle: undefined,
  assigneeCanEdit: false,
  followerCanEdit: false,
  haveConversation: false,
  effectiveDateType: 'dueDate',
  printTemplateKeys: undefined,
  onlyVerifierCanMark: false,
  relatedProviderItemTitle: undefined,
  addCreatorToFollowers: false,
  allowDirectPublish: true,
  notificationProperties: {
    notifyWithSMS: 'None',
    notifyPastEvents: false,
    alarmProgress: false,
    assignmentMessageAll: undefined,
    assignmentMessageSelf: undefined,
    verifierMessageSelf: undefined,
    unAssignmentMessageSelf: undefined,
    assignmentMessageProgressDoes: undefined,
    assignmentMessageProgressVerifier: undefined,
    assignmentMessageProgressDoesByOther: undefined,
    assignmentMessageProgressVerifierByOther: undefined,
    undraftAllSubTasksNotificationMessage: undefined,
    undraftAllSubTasksNotificationMessageToUsers: undefined,
    taskIsReadyToPublishNotificationMessage: undefined,
    subTasksAreReadyToPublishNotificationMessage: undefined,
    fieldNotifications: [],
  },
  taskProgressType: {
    main: null,
    secondary: null,
  },
  templateReportOptions: {
    verifyTitle: undefined,
    mainProgressTitle: undefined,
    averageProgressMain: undefined,
    secondaryProgressTitle: undefined,
    completedTaskCountMain: undefined,
    inProgressTaskCountMain: undefined,
    notStartedTaskCountMain: undefined,
    averageProgressSecondary: undefined,
    completedAssignmentCount: undefined,
    inProgressAssignmentCount: undefined,
    notStartedAssignmentCount: undefined,
    averageAssignmentProgress: undefined,
    rejectedTaskCountVerifier: undefined,
    completedTaskCountVerifier: undefined,
    notStartedTaskCountVerifier: undefined,
    completedTaskCountSecondary: undefined,
    inProgressTaskCountSecondary: undefined,
    notStartedTaskCountSecondary: undefined,
  },
};
type PrintPartAvailability = 'body' | 'head' | 'subtask';
type PrintOption = IOption & {
  available: PrintPartAvailability[];
  type?: string;
};
const printTemplateParts: PrintOption[] = [
  {
    value: 'ProjectName',
    label: i18n.t('ProjectName', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'SubTasksTitle',
    label: i18n.t('SubTasksTitle', { ns: 'companyManagement' }),
    available: ['body', 'head', 'subtask'],
  },
  {
    value: 'Title',
    label: i18n.t('title', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentTitle',
    label: i18n.t('parentTitle', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'AncestorTitle',
    label: i18n.t('ancestorTitle', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'Verifier',
    label: i18n.t('Verifier', { ns: 'companyManagement' }),
    available: ['body', 'head', 'subtask'],
  },
  {
    value: 'Follower',
    label: i18n.t('Follower', { ns: 'companyManagement' }),
    available: ['body', 'head', 'subtask'],
  },
  {
    value: 'Publisher',
    label: i18n.t('Publisher', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'SubTaskPublisher',
    label: i18n.t('SubTaskPublisher', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentVerifier',
    label: i18n.t('ParentVerifier', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentFollower',
    label: i18n.t('ParentFollower', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentPublisher',
    label: i18n.t('ParentPublisher', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentSubTaskPublisher',
    label: i18n.t('ParentSubTaskPublisher', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'StartDate',
    label: i18n.t('StartDate', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'StartTime',
    label: i18n.t('StartTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'StartDateTime',
    label: i18n.t('StartDateTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'DueDate',
    label: i18n.t('DueDate', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'DueTime',
    label: i18n.t('DueTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'Weight',
    label: i18n.t('Weight', { ns: 'companyManagement' }),
    available: ['subtask', 'head'],
  },
  {
    value: 'DueDateTime',
    label: i18n.t('DueDateTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentStartDate',
    label: i18n.t('ParentStartDate', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentStartTime',
    label: i18n.t('ParentStartTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentStartDateTime',
    label: i18n.t('ParentStartDateTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentDueDate',
    label: i18n.t('ParentDueDate', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentDueTime',
    label: i18n.t('ParentDueTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'ParentDueDateTime',
    label: i18n.t('ParentDueDateTime', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'DateCombo',
    label: i18n.t('DateCombo', { ns: 'companyManagement' }),
    available: ['body', 'head', 'subtask'],
  },
  {
    value: 'ParentDateCombo',
    label: i18n.t('ParentDateCombo', { ns: 'companyManagement' }),
    available: ['body', 'head'],
  },
  {
    value: 'NewLine0',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'NewLine1',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'NewLine2',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'NewLine3',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'NewLine4',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'NewLine5',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'NewLine6',
    label: i18n.t('NewLine', { ns: 'companyManagement' }),
    available: ['head'],
  },
  {
    value: 'Assignee',
    label: i18n.t('Assignee', { ns: 'companyManagement' }),
    available: ['subtask', 'head', 'body'],
  },
  {
    value: 'Subtasks',
    label: i18n.t('Subtasks', { ns: 'companyManagement' }),
    available: ['body'],
  },
  {
    value: 'Signatures',
    label: i18n.t('Signatures', { ns: 'companyManagement' }),
    available: ['body'],
  },
  {
    value: 'MainProgress',
    label: i18n.t('MainProgress', { ns: 'companyManagement' }),
    available: ['head', 'body', 'subtask'],
  },
  {
    value: 'ParentMainProgress',
    label: i18n.t('ParentMainProgress', { ns: 'companyManagement' }),
    available: ['head', 'body'],
  },
  {
    value: 'SecondaryProgress',
    label: i18n.t('SecondaryProgress', { ns: 'companyManagement' }),
    available: ['head', 'body', 'subtask'],
  },
  {
    value: 'ParentSecondaryProgress',
    label: i18n.t('ParentSecondaryProgress', { ns: 'companyManagement' }),
    available: ['head', 'body'],
  },
  {
    value: 'RelatedProviderItem',
    label: i18n.t('RelatedProviderItem', { ns: 'companyManagement' }),
    available: ['subtask', 'body', 'head'],
  },
  {
    value: 'SubtasksAndProgress',
    label: i18n.t('SubTaskAndProgress', { ns: 'companyManagement' }),
    available: ['body'],
  },
];

class CompanyTemplateEditor extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  extractPrintTemplateValues = (
    printTemplateString: string,
    availableOptions: PrintOption[]
  ): {
    printBodyValues: PrintOption[];
    printHeaderValues: PrintOption[];
    printSubTasksValues: PrintOption[];
    successfulMatch: boolean;
  } => {
    const printTemplateObject = JSON.parse(
      printTemplateString
    ) as ITaskPrintTemplateObject;
    const ret: {
      printBodyValues: PrintOption[];
      printHeaderValues: PrintOption[];
      printSubTasksValues: PrintOption[];
      successfulMatch: boolean;
    } = {
      printBodyValues: [],
      printHeaderValues: [],
      printSubTasksValues: [],
      successfulMatch: true,
    };

    function findMatchingItemOrCreate(printPart: string, availablity: any) {
      const migrateMatch = (s: string) => (s === 'verifier' ? 'verifiers' : s);
      return (
        availableOptions.find(
          (printOption) => printOption.value === migrateMatch(printPart)
        ) || {
          value: printPart,
          label: printPart,
          available: [availablity],
        }
      );
    }

    printTemplateObject?.body?.forEach((value) => {
      const result: string[] = this.getPrintOptionKey(value);
      result
        .filter((it) => it !== 'NewLine')
        .forEach((printPart) => {
          ret.printBodyValues.push(findMatchingItemOrCreate(printPart, 'body'));
        });
    });
    let foundNewLines = 0;
    printTemplateObject?.header?.forEach((value) => {
      const result: string[] = this.getPrintOptionKey(value);
      result.forEach((printPart) => {
        let finalPrintPart = printPart;
        if (printPart.startsWith('NewLine')) {
          finalPrintPart = printPart + foundNewLines;
          foundNewLines++;
        }
        ret.printHeaderValues.push(
          findMatchingItemOrCreate(finalPrintPart, 'header')
        );
      });
    });
    (
      printTemplateObject?.body?.find((it) => it.part === 'subTasks')
        ?.options as any
    )?.tableBody?.forEach((value: any) => {
      const result: string[] = this.getPrintOptionKey(value);
      result.forEach((printPart) => {
        ret.printSubTasksValues.push(
          findMatchingItemOrCreate(printPart, 'header')
        );
      });
    });

    return ret;
  };

  private getPrintOptionKey(
    value:
      | IPrintTemplatePartObject<PrintTaskParts, 'body'>
      | IPrintTemplatePartObject<PrintTaskParts, 'header'>
  ) {
    const isParent = value.ref === 'parentTask';
    const isAncestor = value.ref === 'ancestorTask';
    let result: string = 'Unknown';
    const hasNewLine = value.options.newLine;
    switch (value.part as any) {
      case 'fields':
        result = 'Field@' + (value as any)?.options?.customTitle;
        break;
      case 'assignee':
        result = 'Assignee';
        break;
      case 'title':
        result = 'Title';
        break;
      case 'ancestorTitle':
        result = 'AncestorTitle';
        break;
      case 'parentTitle':
        result = 'ParentTitle';
        break;
      case 'followers':
        result = 'Follower';
        break;
      case 'weight':
        result = 'Weight';
        break;
      case 'dueDate':
        result = 'DueDate';
        if ((value.options as any).format === 'hours') result = 'DueTime';
        break;
      case 'dateCombo':
        result = 'DateCombo';
        break;
      case 'verifiers':
      case 'verifier':
        result = 'Verifier';
        break;
      case 'taskPublisher':
        result = 'Publisher';
        break;
      case 'subTaskPublisher':
        result = 'SubTaskPublisher';
        break;
      case 'projectId':
        result = 'ProjectName';
        break;
      case 'startDate':
        result = 'StartDate';
        if ((value.options as any).format === 'hours') result = 'StartTime';
        break;
      case 'subTasks':
        result = `Subtasks${value.options.includeProgress ? 'AndProgress' : ''}`;
        break;
      case 'signatures':
        result = 'Signatures';
        break;
      case 'subTasksTitle':
        result = 'Title';
        break;
      case 'mainProgress':
        result = 'MainProgress';
        break;
      case 'secondaryProgress':
        result = 'SecondaryProgress';
        break;
      case 'relatedProviderItemId':
        result = 'RelatedProviderItem';
        break;
    }
    let ret: string[];
    if (result === 'Unknown') window.alert(JSON.stringify(value));
    if (result.startsWith('Parent') || result.startsWith('Ancestor')) {
      ret = [result];
    } else {
      ret = [(isParent ? 'Parent' : isAncestor ? 'Ancestor' : '') + result];
    }
    if (hasNewLine) ret.push('NewLine');
    return ret;
  }

  constructor(props: IComponentProps) {
    super(props);
    let subTemplates: IOption[] = [];
    if (props.templateId) {
      const templateId = Number(props.templateId);
      subTemplates = props.company.templates[templateId].children!.reduce(
        (list: IOption[], taskId) => {
          const task = props.tasks.data[taskId];
          const item: IOption = { value: taskId, label: task.taskName };
          list.push(item);
          return list;
        },
        []
      );
    }
    let printTemplateKeys;
    if (props.template && props.template.printTemplate) {
      const t = props.template.printTemplate;
      printTemplateKeys = JSON.stringify(
        typeof t === 'string'
          ? JSON.parse(props.template.printTemplate)
          : props.template.printTemplate
      );
    }
    const printTemplateValues = this.extractPrintTemplateValues(
      printTemplateKeys || '{}',
      this.getAvailablePrintParts(subTemplates)
    );
    this.state = {
      saving: false,
      loading: true,
      suggestOnDocumentManager: this.props.template
        ? this.props.company.templates[this.props.template.id]
            .suggestOnDocumentManager
        : false,
      subTemplates,
      applyTemplate: false,
      printTemplateKeys,
      template: props.template ? props.template : (initialTemplate as ITask),
      printBodyValues: printTemplateValues.printBodyValues,
      printHeaderValues: printTemplateValues.printHeaderValues,
      printSubtasksValues: printTemplateValues.printSubTasksValues,
      compulsoryFields: this.props.template?.requiredFields || [],
    };
  }

  componentDidMount() {
    const isSupport = isCompanySupport(this.props.thisUser, this.props.company);
    const isOwner = isCompanyOwner(this.props.thisUser, this.props.company);
    const isAdvancedAdmin = isAdvanceAdmin(
      this.props.thisUser,
      this.props.company
    );
    if (!isOwner && !isSupport && !isAdvancedAdmin) {
      navigate('/notfound');
    }
    const members = Object.keys(this.props.company.members).map((it) =>
      Number(it)
    );
    this.props
      .requireSeveralUser(members)
      .then(() => {
        this.setState({ loading: false });
      })
      .catch((err: any) => {
        display(err);
      });
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    if (target.dataset.invert) {
      value = !value;
    }
    if (target.name === 'progressiveOrNot') {
      this.setState((prevState) => {
        const type = value ? 'progressive' : 'atOnce';
        return { template: { ...prevState.template, type } };
      });
    } else if (target.name === 'applyTemplate') {
      this.setState((prevState) => ({
        applyTemplate: !prevState.applyTemplate,
      }));
    } else if (target.name === 'suggestOnDocumentManager') {
      this.setState((prevState) => ({
        suggestOnDocumentManager: !prevState.suggestOnDocumentManager,
      }));
    } else {
      this.setState((prevState) => ({
        template: { ...prevState.template, [target.name]: value },
      }));
    }
  };

  handleChangeNotificationProperties = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.setState((prevState) => ({
      template: {
        ...prevState.template,
        notificationProperties: {
          ...prevState.template.notificationProperties,
          [target.name]: value,
        },
      },
    }));
  };
  handleChangeSelectNotificationProperties = (key: string, value: string) => {
    this.setState((prevState) => ({
      template: {
        ...prevState.template,
        notificationProperties: {
          ...prevState.template.notificationProperties,
          [key]: value,
        },
      },
    }));
  };

  handleUpdateFieldNotificationProperties = (
    index: number,
    obj: IFieldNotification
  ) => {
    this.setState((prevState) => {
      return {
        template: {
          ...prevState.template,
          notificationProperties: {
            ...prevState.template.notificationProperties,
            fieldNotifications:
              prevState.template.notificationProperties.fieldNotifications.map(
                (item, ind) => {
                  return index === ind ? obj : item;
                }
              ),
          },
        },
      };
    });
  };

  handleAddFieldNotifications = (obj: IFieldNotification) => {
    this.setState((prevState) => {
      return {
        template: {
          ...prevState.template,
          notificationProperties: {
            ...prevState.template.notificationProperties,
            fieldNotifications: [
              ...(prevState.template.notificationProperties
                ?.fieldNotifications || []),
              obj,
            ],
          },
        },
      };
    });
  };

  handleRemoveFieldNotifications = (ind: number) => {
    this.setState((prevState) => {
      return {
        template: {
          ...prevState.template,
          notificationProperties: {
            ...prevState.template.notificationProperties,
            fieldNotifications:
              prevState.template.notificationProperties.fieldNotifications.filter(
                (_, index) => index !== ind
              ),
          },
        },
      };
    });
  };

  handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const target = event.target;
    const value = target.value;
    this.setState((prevState) => ({
      template: { ...prevState.template, [target.name]: value },
    }));
  };

  applyTemplateToPastTasks = (templateId: number) => {
    this.props.applyTemplateToPastTasks(templateId).then(
      ({ countOfAppliedTasks: num }) => {
        notify(messages.templateChangesAppliedToTasks(num));
        navigate(`/t/${this.props.companyId}/admin/template/list`);
      },
      () => {
        notify(
          i18n.t('applyTemplateFailed', { ns: 'companyManagement' }),
          'error'
        );
        this.setState({ saving: false });
      }
    );
  };

  copyTemplateInfo = (e: React.FormEvent) => {
    e.preventDefault();
    const companyTask = this.createTemplateFromForm();
    setClipboard(JSON.stringify(companyTask));
    notify(i18n.t('copied', { ns: 'companyManagement' }));
  };
  pasteTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    navigator.clipboard.readText().then((text) => {
      const companyTemplateObj = JSON.parse(text);
      if (!companyTemplateObj?.task?.id) {
        alert(i18n.t('noTemplateFount', { ns: 'companyManagement' }));
        return;
      }
      const templateName = companyTemplateObj?.task?.taskName;
      if (confirm(messages.confirmPaste(templateName))) {
        let template = companyTemplateObj.task;
        if (companyTemplateObj.companyId !== Number(this.props.companyId)) {
          template.verifiers = null;
          template.taskPublisher = null;
          template.subTaskPublisher = null;
        } else {
          template.verifiers?.forEach((it: IAssignmentView) => {
            it.taskId = undefined;
          });
          if (template.taskPublisher) template.taskPublisher.taskId = undefined;
          if (template.subTaskPublisher)
            template.subTaskPublisher.taskId = undefined;
        }
        template = serverToStore(template) as ITask;
        template.assignee = [];
        template.followers = [];
        template.id = this.state.template?.id;
        template.fields?.forEach((f: IField) => {
          const exists = this.props.template?.fields?.find(
            (it) => it.title === f.title
          );
          if (exists) f.name = exists.name;
        });

        const printTemplateKeys = template.printTemplate
          ? JSON.stringify(template.printTemplate)
          : '{}';
        const printTemplateValues = this.extractPrintTemplateValues(
          printTemplateKeys || '{}',
          this.getAvailablePrintParts([], template as ITask)
        );
        this.setState({
          template,
          printTemplateKeys: printTemplateKeys,
          suggestOnDocumentManager: companyTemplateObj.suggestOnDocumentManager,
          printBodyValues: printTemplateValues.printBodyValues,
          printHeaderValues: printTemplateValues.printHeaderValues,
          printSubtasksValues: printTemplateValues.printSubTasksValues,
        });
        notify(i18n.t('pasted', { ns: 'companyManagement' }));
      }
    });
  };

  convertPrintPartsToJson = (
    header: PrintOption[],
    body: PrintOption[],
    subTask: PrintOption[]
  ) => {
    if (body.length === 0) {
      return {};
    }
    const res: ITaskPrintTemplateObject = { body: [], header: [] };

    const convertPrintItemToJson = (
      item: string,
      place: 'body' | 'header' | 'subTaskHeader' | 'subTaskBody',
      type: string | undefined
    ): any => {
      const tempType = place === 'header' ? 'header' : 'body';
      let result = {} as IPrintTemplatePartObject<
        PrintTaskParts,
        typeof tempType
      >;
      const defaultOptions = {
        newLine: false,
        view: 'both' as any,
      };
      switch (item) {
        case 'ProjectName':
          result = {
            part: 'projectId',
            ref: 'selfTask',
            options: { ...defaultOptions },
          };
          break;
        case 'Weight':
          result = {
            part: 'weight',
            ref: 'selfTask',
            options: { ...defaultOptions },
          };
          break;
        case 'Verifier':
        case 'ParentVerifier':
          result = {
            part: 'verifiers',
            ref: 'selfTask',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'Follower':
        case 'ParentFollower':
          result = {
            part: 'followers',
            ref: 'selfTask',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'Publisher':
        case 'ParentPublisher':
          result = {
            part: 'taskPublisher',
            ref: 'selfTask',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'SubTaskPublisher':
        case 'ParentSubTaskPublisher':
          result = {
            part: 'subTaskPublisher',
            ref: 'selfTask',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'StartDate':
        case 'ParentStartDate':
          result = {
            part: 'startDate',
            ref: 'selfTask',
            options: {
              format: 'date',
              ...defaultOptions,
            },
          };
          break;
        case 'StartTime':
        case 'ParentStartTime':
          result = {
            part: 'startDate',
            ref: 'selfTask',
            options: {
              format: 'hours',
              ...defaultOptions,
              customTitle: i18n.t('StartTime', {
                ns: 'companyManagement',
              }),
            },
          };
          break;
        // case 'StartDateTime':
        // case 'ParentStartDateTime':
        //   result = {
        //     part: 'startDate',
        //     ref: 'selfTask',
        //     options: {
        //       format: 'date',
        //       ...defaultOptions
        //     },
        //   };
        //   break;
        case 'DueDate':
        case 'ParentDueDate':
          result = {
            part: 'dueDate',
            ref: 'selfTask',
            options: {
              format: 'date',
              ...defaultOptions,
            },
          };
          break;
        case 'DueTime':
        case 'ParentDueTime':
          result = {
            part: 'dueDate',
            ref: 'selfTask',
            options: {
              format: 'hours',
              ...defaultOptions,
              customTitle: i18n.t('DueTime', {
                ns: 'companyManagement',
              }),
            },
          };
          break;
        // case 'DueDateTime':
        // case 'ParentDueDateTime':
        //   result = {};
        //   break;
        case 'DateCombo':
        case 'ParentDateCombo':
          result = {
            ref: 'selfTask',
            part: 'dateCombo' as any,
            options: {
              format: 'date',
              ...defaultOptions,
            },
          };
          break;
        case 'Assignee':
        case 'ParentAssignee':
          result = {
            ref: 'selfTask',
            part: 'assignee',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'Subtasks':
          result = {
            ref: 'selfTask',
            part: 'subTasks',
            options: {
              ...defaultOptions,
              tableHeader: [],
              tableBody: [],
            },
          };
          break;
        case 'Signatures':
          result = {
            ref: 'selfTask',
            part: 'signatures',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'MainProgress':
          result = {
            ref: 'selfTask',
            part: 'mainProgress',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'ParentMainProgress':
          result = {
            ref: 'parentTask',
            part: 'mainProgress',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'SecondaryProgress':
          result = {
            ref: 'selfTask',
            part: 'secondaryProgress',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'ParentSecondaryProgress':
          result = {
            ref: 'parentTask',
            part: 'secondaryProgress',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'RelatedProviderItem':
          result = {
            ref: 'selfTask',
            part: 'relatedProviderItemId',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'Title':
        case 'SubTasksTitle':
          result = {
            ref: 'selfTask',
            part: place === 'subTaskHeader' ? 'subTasksTitle' : 'title',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'SubtasksAndProgress':
          result = {
            ref: 'selfTask',
            part: 'subTasks',
            options: {
              ...defaultOptions,
              tableHeader: [],
              tableBody: [],
              includeProgress: true,
            },
          };
          break;
        case 'ParentTitle':
          result = {
            ref: 'parentTask',
            part: 'title',
            options: {
              ...defaultOptions,
            },
          };
          break;
        case 'AncestorTitle':
          result = {
            ref: 'ancestorTask',
            part: 'title',
            options: {
              ...defaultOptions,
            },
          };
          break;
        default:
          if (
            item.startsWith('Field') ||
            item.startsWith('ParentField') ||
            item.startsWith('AncestorField')
          ) {
            const fieldName = item.split('@')[1];
            result = {
              ref: 'selfTask',
              part: 'fields',
              options: {
                ...defaultOptions,
                customTitle: fieldName,
              },
            };
            if (type === 'date') {
              result.options['format'] = 'date';
            }
          } else {
            alert('could not convert: ' + item);
          }
          break;
      }
      if (item.startsWith('Parent')) result.ref = 'parentTask';
      else if (item.startsWith('Ancestor')) result.ref = 'ancestorTask';
      if (!result.options) {
        result.options = { ...defaultOptions };
      }
      if (place === 'header') {
        result.options['size'] = 'small';
      } else if (place === 'body') {
        result.options['size'] = 'large';
        result.options.newLine = true;
      } else if (place === 'subTaskBody') {
        result.options['size'] = 'small';
        result.options['view'] = 'value';
        result.ref = 'subTask' as any;
      } else if (place === 'subTaskHeader') {
        result.options['size'] = 'small';
        if (result.part !== 'subTasksTitle') {
          result.options['view'] = 'key';
          result.ref = 'subTask' as any;
        }
      }
      return result;
    };

    header.forEach((item: PrintOption) => {
      if (
        item.value.toString().startsWith('NewLine') &&
        res.header.length > 0
      ) {
        res.header[res.header.length - 1].options.newLine = true;
      } else
        res.header.push(
          convertPrintItemToJson(item.value.toString(), 'header', item.type)
        );
    });
    body.forEach((item: PrintOption) => {
      if (item.value.toString().startsWith('NewLine') && res.body.length > 0) {
        res.body[res.body.length - 1].options.newLine = true;
      } else
        res.body.push(
          convertPrintItemToJson(item.value.toString(), 'body', item.type)
        );
    });
    const subtasks = res.body.find((it) => it.part === 'subTasks');
    if (subtasks) {
      subTask.forEach((item: PrintOption) => {
        subtasks.options['tableHeader'].push(
          convertPrintItemToJson(
            item.value.toString(),
            'subTaskHeader',
            item.type
          )
        );
        subtasks.options['tableBody'].push(
          convertPrintItemToJson(
            item.value.toString(),
            'subTaskBody',
            item.type
          )
        );
      });
    }
    return res;
  };
  handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    this.setState({ saving: true });
    const companyTask = this.createTemplateFromForm();
    if (!this.props.templateId) {
      this.props
        .addTemplate(companyTask)
        .then(() => {
          notify(i18n.t('templateUpdated', { ns: 'companyManagement' }));
          navigate(`/t/${this.props.companyId}/admin/template/list`);
        })
        .catch((err: any) => {
          this.setState({ saving: false });
          display(err);
        });
    } else {
      this.props
        .updateTemplate(companyTask)
        .then(() => {
          notify(i18n.t('templateUpdated', { ns: 'companyManagement' }));
        })
        .then(() => {
          if (this.state.applyTemplate) {
            this.applyTemplateToPastTasks(companyTask.task!.id as number);
          } else {
            navigate(`/t/${this.props.companyId}/admin/template/list`);
          }
        })
        .catch((err: any) => {
          this.setState({ saving: false });
          display(err);
        });
    }
  };

  private createTemplateFromForm() {
    const template = cloneDeep(this.state.template);
    let templateReportOptions: ITemplateReportOptions;
    if (template.templateReportOptions) {
      templateReportOptions = Object.entries(
        template.templateReportOptions
      ).reduce((titles: { [key: string]: StringOrNull }, title) => {
        if (title[1] && title[1].length > 0) {
          titles[title[0]] = title[1];
        } else {
          titles[title[0]] = null;
        }
        return titles;
      }, {});
      template.templateReportOptions = templateReportOptions;
    }

    try {
      template.printTemplate = this.convertPrintPartsToJson(
        this.state.printHeaderValues,
        this.state.printBodyValues,
        this.state.printSubtasksValues
      ) as any;
      if (
        template.printTemplate &&
        !Object.values(template.printTemplate).length
      )
        template.printTemplate = null;
    } catch {
      template.printTemplate = null;
    }

    template.fields = this.state.template.fields.reduce(
      (fields: IField[], field) => {
        const item: IField = {
          ...field,
          options:
            field.options && Object.keys(field.options).length > 0
              ? field.options
              : null,
        };
        fields.push(item);
        return fields;
      },
      []
    );

    if (Object.keys(template.verifiers || {})?.length > 0) {
      template.haveVerifier = true;
    }
    if (template.assigneeTitle === '') {
      template.assigneeTitle = undefined;
    }
    if (template.followersTitle === '') {
      template.followersTitle = undefined;
    }
    if (template.subTasksTitle === '') {
      template.subTasksTitle = undefined;
    }
    if (template.chatTitle === '') {
      template.chatTitle = undefined;
    }
    if (template.dueDateTitle === '') {
      template.dueDateTitle = undefined;
    }
    if (template.startDateTitle === '') {
      template.startDateTitle = undefined;
    }
    if (template.relatedProviderItemTitle === '') {
      template.relatedProviderItemTitle = undefined;
    }

    const newTask = taskManipulate.stateToServer(template);
    const subTemplatesId = this.state.subTemplates.map(
      (subTemplate) => subTemplate.value as number
    );

    const companyTask: ICompanyTemplate = {
      taskId: newTask.id,
      companyId: Number(this.props.companyId),
      task: { ...newTask, requiredFields: this.state.compulsoryFields },
      children: subTemplatesId,
      suggestOnDocumentManager: this.state.suggestOnDocumentManager,
    };
    return companyTask;
  }

  handleCancel = () => {
    navigate(`/t/${this.props.companyId}/admin/template/list`);
  };

  handleChangeVerifier =
    (field: 'verifiers' | 'taskPublisher' | 'subTaskPublisher' | 'assignee') =>
    (values: IOption[]) => {
      const template = cloneDeep(this.state.template);
      const result: IAssignmentView[] = [];
      values.forEach((item) => {
        const valueAsNumber =
          typeof item.value === 'number' ? item.value : Number(item.value);
        const userId = valueAsNumber > 0 ? valueAsNumber : (undefined as any);
        let companyRoleId = undefined as number | undefined;
        let projectRoleId = undefined as number | undefined;
        if (!userId) {
          const roleId = -valueAsNumber;
          if (this.props.company.roles[roleId].isProjectRole) {
            projectRoleId = roleId;
          } else {
            companyRoleId = roleId;
          }
        }
        result.push({
          userId: userId,
          progress: 0,
          status: 'notStarted',
          type: 'verify',
          taskId: template.id as number,
          properties: {
            roleId: companyRoleId,
            projectRoleId,
          },
        });
      });
      if (field === 'assignee') {
        template.assignee = result;
      } else if (field === 'verifiers') {
        template.verifiers = result.reduce(
          (dict, item, index) => {
            const key =
              item.userId ||
              (item.properties?.roleId
                ? -item.properties.roleId
                : item.properties?.projectRoleId
                  ? -item.properties.projectRoleId
                  : index);
            dict[key] = item;
            return dict;
          },
          {} as { [key: number]: IAssignmentView }
        );
      } else {
        template[field] = result[0] || undefined;
      }
      this.setState({ template });
    };

  handleChangeSubTemplates = (values: IOption[]) => {
    this.setState({ subTemplates: values });
  };

  handleChangePrintTemplate = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ printTemplateKeys: event.target.value });
  };

  handleAddReminder = (e: React.MouseEvent) => {
    e.preventDefault();
    const reminder: IReminderInfo = {
      offsetM: -60,
      target: 'assignee',
      timeTarget: 'dueDate',
      message: '',
    };
    const reminderInfos = [...this.state.template.reminderInfos, reminder];
    this.setState({ template: { ...this.state.template, reminderInfos } });
  };

  handleReminderInputChange = (
    reminderIndex: number,
    inputType: 'reminder' | 'target' | 'timeTarget' | 'message',
    value: string
  ) => {
    const reminderInfos = cloneDeep(this.state.template.reminderInfos);
    if (inputType === 'reminder') {
      reminderInfos[reminderIndex].offsetM = Number(value);
    } else if (inputType === 'target') {
      reminderInfos[reminderIndex].target = value as UserType;
    } else if (inputType === 'timeTarget') {
      reminderInfos[reminderIndex].timeTarget = value as TimeType;
    } else if (inputType === 'message') {
      reminderInfos[reminderIndex].message = value as string;
    }
    this.setState({ template: { ...this.state.template, reminderInfos } });
  };

  removeReminder = (reminderIndex: number) => {
    this.setState((prevState) => {
      const template = cloneDeep(prevState.template);
      template.reminderInfos.splice(reminderIndex, 1);
      return { template: { ...template } };
    });
  };

  handleSubmitCustomReminder = (min: number, reminderId: number) => {
    const reminderInfos = cloneDeep(this.state.template.reminderInfos);
    reminderInfos[reminderId].offsetM = min;
    this.setState({ template: { ...this.state.template, reminderInfos } });
  };

  handleAddField = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState((prevState) => {
      const fieldItem: IField = {
        name: (Math.random() + 1).toString(36).substring(2),
        value: '',
        options: null,
      };
      const fields = [...prevState.template.fields, fieldItem];
      return {
        template: { ...prevState.template, fields },
      };
    });
  };

  handleRemoveField = (e: React.MouseEvent<HTMLSpanElement>) => {
    const target = e.currentTarget;
    const fieldName = target.dataset.fieldName;
    this.setState((prevState) => {
      const fields = prevState.template.fields.filter(
        (item) => item.name !== fieldName
      );
      return { template: { ...prevState.template, fields } };
    });
  };

  handleChangeFieldName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const index = Number(target.dataset.fieldIndex);
    const type = target.dataset.fieldType as
      | 'title'
      | 'value'
      | 'required'
      | 'relatedToProgress'
      | 'highlight'
      | 'showInPrint';
    const { value, checked } = target;
    this.setState((prevState) => {
      const fields = cloneDeep(prevState.template.fields);
      if (
        type === 'required' ||
        type === 'relatedToProgress' ||
        type === 'highlight' ||
        type === 'showInPrint'
      ) {
        (fields[index][type] as any) = checked;
      } else (fields[index][type] as any) = value as any;
      return { template: { ...prevState.template, fields } };
    });
  };
  handleChangeFieldType = (key: number, value: IField['type']) => {
    this.setState((prevState) => {
      const fields = cloneDeep(prevState.template.fields);
      fields[key].type = value;
      return { template: { ...prevState.template, fields } };
    });
  };
  handleChangeFieldAutoFillFunc = (
    key: number,
    value: IField['autoFillFunc']
  ) => {
    this.setState((prevState) => {
      const fields = cloneDeep(prevState.template.fields);
      fields[key].autoFillFunc = value;
      return { template: { ...prevState.template, fields } };
    });
  };

  handleChangeFieldOptions = (fieldIndex: number) => (options: IOption[]) => {
    this.setState((prevState) => {
      const fields = cloneDeep(prevState.template.fields);
      const field = fields[fieldIndex];
      const newOptions = options.reduce((map, option) => {
        map[option.value] = option.value;
        return map;
      }, {});
      field.options = options.length > 0 ? newOptions : null;
      fields[fieldIndex] = field;
      return { template: { ...prevState.template, fields } };
    });
  };

  handleChangeFieldConfigs = (fieldIndex: number) => (configs: IOption[]) => {
    this.setState((prevState) => {
      const fields = cloneDeep(prevState.template.fields);
      const field = fields[fieldIndex];
      const newConfigs = configs.map((option) => {
        return option.value.toString();
      });
      field.configs = newConfigs;
      fields[fieldIndex] = field;
      return { template: { ...prevState.template, fields } };
    });
  };

  handleMoveField = (sourceIndex: number, destIndex: number) => {
    this.setState((prevState) => {
      const fields = cloneDeep(prevState.template.fields);
      moveItemInArray(fields, sourceIndex, destIndex);
      return { template: { ...prevState.template, fields } };
    });
  };

  handleChangeReportOption = (title: string, value: string | boolean) => {
    this.setState((prevState) => {
      const template = cloneDeep(prevState.template);
      template.templateReportOptions = {
        ...template.templateReportOptions,
        [title]: value as boolean,
      };
      return { template };
    });
  };

  handleChangeProgressType = (name: string, value: string) => {
    this.setState((prevState) => {
      const template = cloneDeep(prevState.template);
      template.taskProgressType[name as 'main' | 'secondary'] =
        value as ITaskProgressType['main'];
      return { template };
    });
  };

  getAvailablePrintParts = (
    subTemplate: IOption[],
    templ: ITask | undefined = undefined
  ) => {
    const { company, tasks, templateId } = this.props;
    const template =
      templ ??
      ((this.props.template ? this.props.template : initialTemplate) as ITask);
    const subTemplates: ITask[] = [];
    const parentTemplates: ITask[] = [];
    const ancestorsTemplates: ITask[] = [];
    Object.values(company.templates).forEach((companyTemplate) => {
      const templateItem = tasks.data[companyTemplate.taskId];
      if (companyTemplate.children?.includes(Number(templateId)))
        parentTemplates.push(templateItem);
      if (subTemplate?.find((it) => it.value === Number(templateItem.id)))
        subTemplates.push(templateItem);
      for (const child of companyTemplate.children!) {
        if (company.templates?.[child]?.children?.includes(Number(templateId)))
          ancestorsTemplates.push(templateItem);
      }
    });
    const ret = [...printTemplateParts];
    subTemplates.forEach((templateItem) => {
      templateItem.fields.forEach((field) => {
        ret.push({
          value: `Field@${field.name}`,
          label: field.title || field.name,
          available: ['subtask'],
          type: field.type,
        });
      });
    });
    parentTemplates.forEach((templateItem) => {
      templateItem.fields.forEach((field) => {
        ret.push({
          value: `ParentField@${field.name}`,
          label:
            (field.title || field.name) +
            i18n.t('from', { ns: 'companyManagement' }) +
            templateItem.taskName,
          available: ['head', 'body'],
          type: field.type,
        });
      });
    });
    // ancestors part
    ancestorsTemplates.forEach((templateItem) => {
      templateItem.fields.forEach((field) => {
        ret.push({
          value: `AncestorField@${field.name}`,
          label:
            (field.title || field.name) +
            i18n.t('from', { ns: 'companyManagement' }) +
            templateItem.taskName,
          available: ['head', 'body'],
          type: field.type,
        });
      });
    });
    //
    template.fields.forEach((field) => {
      ret.push({
        value: `Field@${field.name}`,
        label: field.title || field.name,
        available: ['head', 'body'],
        type: field.type,
      });
    });
    return ret;
  };

  handleChangeCompulsoryFields = (values: IOption[]) => {
    this.setState({
      compulsoryFields: values.map((field) => field.value.toString()),
    });
  };

  render() {
    if (this.state.loading) {
      return <LogoLoading />;
    }
    const { company, tasks, users } = this.props;
    const {
      saving,
      template,
      subTemplates,
      applyTemplate,
      printHeaderValues,
      printSubtasksValues,
      printBodyValues,
    } = this.state;
    const availablePrintParts = this.getAvailablePrintParts(subTemplates);
    // useMemo(this.getAvailablePrintParts, [
    //   subTemplates,
    //   company,
    //   ...template.fields,
    // ]);

    const companyTemplates: IOption[] = Object.values(company.templates).reduce(
      (list: IOption[], companyTemplate) => {
        const templateItem = tasks.data[companyTemplate.taskId];
        const item: IOption = {
          value: templateItem?.id,
          label: templateItem?.archived
            ? `${templateItem.taskName} (${i18n.t('archived', { ns: 'companyManagement' })})`
            : templateItem?.taskName || '',
        };
        list.push(item);
        return list;
      },
      []
    );
    const companyMembers: IOption[] = Object.values(company.members).reduce(
      (list: IOption[], companyMember) => {
        const item: IOption = {
          value: companyMember.userId,
          label: users.map[companyMember.userId].name,
        };
        if (!companyMember.inactive) list.push(item);
        return list;
      },
      []
    );
    Object.values(company.roles).forEach((role) => {
      const item: IOption = {
        value: -role.id,
        label: role.displayName!,
      };
      companyMembers.push(item);
    }, []);

    const selectedVerifiers = this.getSelectedVerifiersAsOption(
      this.state.template.verifiers,
      users,
      company
    );
    const selectedTaskPublisher = this.getSelectedVerifierAsOption(
      this.state.template.taskPublisher,
      users,
      company
    );
    const selectedSubTaskPublisher = this.getSelectedVerifierAsOption(
      this.state.template.subTaskPublisher,
      users,
      company
    );
    const selectedAssignees = this.getSelectedAssigneeAsOption(
      this.state.template.assignee,
      users,
      company
    );

    const floatDir = languageDirection() === 'rtl' ? 'right' : 'left';

    const compulsoryFieldsTitle = [
      'assignee',
      'verifier',
      'follower',
      'publisher',
      'subtaskPublisher',
      'startDate',
      'dueDate',
      'relatedItem',
    ];

    const compulsoryFieldsOptions = compulsoryFieldsTitle.reduce(
      (acc: IOption[], title) => {
        if (title === 'assignee' && this.state.template.haveAssignee) {
          acc.push({
            value: 'assignee',
            label: this.state.template.assigneeTitle || messages.assignee,
          });
        } else if (title === 'verifier' && this.state.template.haveVerifier) {
          acc.push({
            value: 'verifier',
            label: this.state.template.verifierTitle || messages.verifier,
          });
        } else if (title === 'follower' && this.state.template.haveFollower) {
          acc.push({
            value: 'follower',
            label: this.state.template.followersTitle || messages.follower,
          });
        } else if (
          title === 'publisher' &&
          this.state.template.haveTaskPublisher
        ) {
          acc.push({
            value: 'publisher',
            label: this.state.template.taskPublisherTitle || messages.publisher,
          });
        } else if (
          title === 'subtaskPublisher' &&
          this.state.template.haveSubTaskPublisher
        ) {
          acc.push({
            value: 'subtaskPublisher',
            label:
              this.state.template.subTaskPublisherTitle ||
              messages.subtaskPublisher,
          });
        } else if (title === 'startDate' && this.state.template.haveStartDate) {
          acc.push({
            value: 'startDate',
            label: this.state.template.startDateTitle || messages.startDate,
          });
        } else if (title === 'dueDate' && this.state.template.haveDueDate) {
          acc.push({
            value: 'dueDate',
            label: this.state.template.dueDateTitle || messages.dueDate,
          });
        } else if (
          title === 'relatedItem' &&
          this.state.template.haveRelatedItem
        ) {
          acc.push({
            value: 'relatedItem',
            label:
              this.state.template.relatedProviderItemTitle || messages.dueDate,
          });
        }
        return acc;
      },
      []
    );

    const partentOptions = [
      {
        value: 1,
        label: i18n.t('parentTaskName', {
          ns: 'companyManagement',
        }),
      },
      {
        value: 2,
        label: i18n.t('AncestorTaskName', {
          ns: 'companyManagement',
        }),
      },
    ];
    return (
      <React.Fragment>
        <Helmet>
          <title>
            {i18n.t('templateEditor', { ns: 'companyManagement' }) as string}
          </title>
          <meta
            name="description"
            content={
              i18n.t('templateEditor', { ns: 'companyManagement' }) as string
            }
          />
        </Helmet>

        <form
          onSubmit={this.handleSave}
          className="section"
          style={{ overflow: 'auto' }}
        >
          <div className="row">
            <div
              className="s12 m6"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Input
                id="taskName"
                type="text"
                name="taskName"
                value={template.taskName}
                label={
                  i18n.t('taskName', { ns: 'companyManagement' }) as string
                }
                required={true}
                onChange={this.handleInputChange}
                placeholder={
                  i18n.t('taskName', { ns: 'companyManagement' }) as string
                }
                autoComplete="off"
              />
              <div>
                <Button color="blue-outline" onClick={this.copyTemplateInfo}>
                  <Icon type="copy" />
                </Button>
                <Button color="red-outline" onClick={this.pasteTemplate}>
                  <Icon type="clipboard" />
                </Button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col s12 m6">
              <Input
                id="followerCanEdit"
                type="checkbox"
                name="followerCanEdit"
                label={
                  i18n.t('followerCanEdit', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.followerCanEdit}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="assigneeCanEdit"
                type="checkbox"
                name="assigneeCanEdit"
                label={
                  i18n.t('assigneeCanEdit', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.assigneeCanEdit}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="progressiveOrNot"
                type="checkbox"
                name="progressiveOrNot"
                label={
                  i18n.t('progressiveOrNot', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.type === 'progressive'}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveVerifier"
                type="checkbox"
                name="haveVerifier"
                label={
                  i18n.t('haveVerifier', { ns: 'companyManagement' }) as string
                }
                checked={template.haveVerifier}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveTaskPublisher"
                type="checkbox"
                name="haveTaskPublisher"
                label={
                  i18n.t('haveTaskPublisher', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.haveTaskPublisher}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveSubTaskPublisher"
                type="checkbox"
                name="haveSubTaskPublisher"
                label={
                  i18n.t('haveSubTaskPublisher', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.haveSubTaskPublisher}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="onlyPublisherCanPublish"
                type="checkbox"
                name="onlyPublisherCanPublish"
                label={
                  i18n.t('onlyPublisherCanPublish', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.onlyPublisherCanPublish}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="allowDirectPublish"
                type="checkbox"
                name="allowDirectPublish"
                label={
                  i18n.t('allowDirectPublish', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.allowDirectPublish}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="allowPublishSubtasks"
                type="checkbox"
                name="allowPublishSubtasks"
                label={
                  i18n.t('allowPublishSubtasks', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.allowPublishSubtasks}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="autoLockOnPublish"
                type="checkbox"
                name="autoLockOnPublish"
                label={
                  i18n.t('autoLockOnPublish', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.autoLockOnPublish}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveFollower"
                type="checkbox"
                name="haveFollower"
                label={
                  i18n.t('haveFollower', { ns: 'companyManagement' }) as string
                }
                checked={template.haveFollower}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveAssignee"
                type="checkbox"
                name="haveAssignee"
                label={
                  i18n.t('haveAssignee', { ns: 'companyManagement' }) as string
                }
                checked={template.haveAssignee}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveConversation"
                type="checkbox"
                name="haveConversation"
                label={
                  i18n.t('haveConversation', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.haveConversation}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveStartDate"
                type="checkbox"
                name="haveStartDate"
                label={
                  i18n.t('haveStartDate', { ns: 'companyManagement' }) as string
                }
                checked={template.haveStartDate}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveVerifyDate"
                type="checkbox"
                name="haveVerifyDate"
                label={
                  i18n.t('haveVerifyDate', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.haveVerifyDate}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveDueDate"
                type="checkbox"
                name="haveDueDate"
                label={
                  i18n.t('haveDueDate', { ns: 'companyManagement' }) as string
                }
                checked={template.haveDueDate}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="dateAndTime"
                type="checkbox"
                name="dateAndTime"
                label={
                  i18n.t('dateAndTime', { ns: 'companyManagement' }) as string
                }
                checked={template.dateAndTime}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="haveSubTask"
                type="checkbox"
                name="haveSubTask"
                label={
                  i18n.t('haveSubTask', { ns: 'companyManagement' }) as string
                }
                checked={template.haveSubTask}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12 m6">
              <Input
                id="preventSubTaskCreationWhenDraft"
                type="checkbox"
                name="preventSubTaskCreationWhenDraft"
                label={
                  i18n.t('preventSubTaskCreationWhenDraft', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.preventSubTaskCreationWhenDraft}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="useParentAssignees"
                type="checkbox"
                name="useParentAssignees"
                label={
                  i18n.t('useParentAssignees', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.useParentAssignees}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="useParentVerifier"
                type="checkbox"
                name="useParentVerifier"
                label={
                  i18n.t('useParentVerifier', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.useParentVerifier}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="haveWeight"
                type="checkbox"
                name="haveWeight"
                label={
                  i18n.t('haveWeight', { ns: 'companyManagement' }) as string
                }
                checked={template.haveWeight}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="disallowQuickAdd"
                type="checkbox"
                name="disallowQuickAdd"
                label={
                  i18n.t('allowQuickAdd', { ns: 'companyManagement' }) as string
                }
                checked={!template.disallowQuickAdd}
                onChange={this.handleInputChange}
                data-invert={true}
              />
            </div>
            <div className="col s6">
              <Input
                id="onlyVerifierCanMark"
                type="checkbox"
                name="onlyVerifierCanMark"
                label={
                  i18n.t('onlyVerifierCanMark', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={!template.onlyVerifierCanMark}
                onChange={this.handleInputChange}
                data-invert={true}
              />
            </div>
            <div className="col s6">
              <Input
                id="addCreatorToFollowers"
                type="checkbox"
                name="addCreatorToFollowers"
                label={
                  i18n.t('addCreatorToFollowers', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.addCreatorToFollowers}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="draft"
                type="checkbox"
                name="draft"
                label={i18n.t('draft', { ns: 'companyManagement' }) as string}
                checked={template.draft}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="assigneeDefaultToProjectMembers"
                type="checkbox"
                name="assigneeDefaultToProjectMembers"
                label={
                  i18n.t('assigneeDefaultToProjectMembers', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.assigneeDefaultToProjectMembers}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="followerDefaultToProjectMembers"
                type="checkbox"
                name="followerDefaultToProjectMembers"
                label={
                  i18n.t('followerDefaultToProjectMembers', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.followerDefaultToProjectMembers}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="haveRelatedItem"
                type="checkbox"
                name="haveRelatedItem"
                label={
                  i18n.t('haveRelatedItem', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.haveRelatedItem}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="verifierCanMark"
                type="checkbox"
                name="verifierCanMark"
                label={
                  i18n.t('verifierCanMark', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.verifierCanMark}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="singleInParent"
                type="checkbox"
                name="singleInParent"
                label={
                  i18n.t('singleInParent', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.singleInParent}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="continueOnSubTasks"
                type="checkbox"
                name="continueOnSubTasks"
                label={
                  i18n.t('continueOnSubTasks', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.continueOnSubTasks}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="haveRelatedTask"
                type="checkbox"
                name="haveRelatedTask"
                label={
                  i18n.t('haveRelatedTask', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.haveRelatedTask}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="allowDuplicate"
                type="checkbox"
                name="allowDuplicate"
                label={
                  i18n.t('allowDuplicate', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.allowDuplicate}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s6">
              <Input
                id="preventReduceProgress"
                type="checkbox"
                name="preventReduceProgress"
                label={
                  i18n.t('preventReduceProgress', {
                    ns: 'companyManagement',
                  }) as string
                }
                checked={template.preventReduceProgress}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="col s12" style={{ marginTop: '0.5rem' }}>
              <div className="col s6">
                <Select
                  values={
                    template.includeParentTaskNames?.map(
                      (it) => partentOptions.find((o) => o.value === it)!
                    ) || []
                  }
                  options={partentOptions}
                  onChangeValues={(options) => {
                    this.setState((prevState) => ({
                      ...prevState,
                      template: {
                        ...prevState.template,
                        includeParentTaskNames: options.map((it) =>
                          Number(it.value)
                        ),
                      },
                    }));
                  }}
                  label={
                    i18n.t('includeParentTaskNameInPreview', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  multiChoice={true}
                />
              </div>
              <div className="col s6">
                <Input
                  id="maxDurationOfAssignmentDate"
                  type="number"
                  value={
                    template.validUpdateDurationInMinutes
                      ? template.validUpdateDurationInMinutes
                      : 0
                  }
                  min={0}
                  label={
                    i18n.t('maxDurationOfAssignmentDate', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  placeholder={i18n.t('maxDurationOfAssignmentDate', {
                    ns: 'companyManagement',
                  })}
                  onChange={(e) => {
                    this.setState((prevState) => ({
                      ...prevState,
                      template: {
                        ...prevState.template,
                        validUpdateDurationInMinutes: Number(e.target.value),
                      },
                    }));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col s12 m6">
              <Select
                values={selectedAssignees}
                options={companyMembers}
                onChangeValues={this.handleChangeVerifier('assignee')}
                placeholder={
                  i18n.t('chooseAssignee', {
                    ns: 'companyManagement',
                  }) as string
                }
                label={
                  i18n.t('defaultAssignee', {
                    ns: 'companyManagement',
                  }) as string
                }
                multiChoice={true}
              />
            </div>
            <div className="col s12 m6">
              <Select
                values={selectedVerifiers}
                options={companyMembers}
                onChangeValues={this.handleChangeVerifier('verifiers')}
                placeholder={
                  i18n.t('chooseVerifier', {
                    ns: 'companyManagement',
                  }) as string
                }
                label={
                  i18n.t('defaultVerifier', {
                    ns: 'companyManagement',
                  }) as string
                }
                multiChoice={true}
              />
            </div>
            <div className="col s12 m6">
              <Select
                values={selectedTaskPublisher}
                options={companyMembers}
                onChangeValues={this.handleChangeVerifier('taskPublisher')}
                placeholder={
                  i18n.t('chooseTaskPublisher', {
                    ns: 'companyManagement',
                  }) as string
                }
                label={
                  i18n.t('defaultTaskPublisher', {
                    ns: 'companyManagement',
                  }) as string
                }
              />
            </div>
            <div className="col s12 m6">
              <Select
                values={selectedSubTaskPublisher}
                options={companyMembers}
                onChangeValues={this.handleChangeVerifier('subTaskPublisher')}
                placeholder={
                  i18n.t('chooseSubTaskPublisher', {
                    ns: 'companyManagement',
                  }) as string
                }
                label={
                  i18n.t('defaultSubTaskPublisher', {
                    ns: 'companyManagement',
                  }) as string
                }
              />
            </div>
            <div className="col s12 m6">
              <Select
                values={subTemplates}
                options={companyTemplates}
                onChangeValues={this.handleChangeSubTemplates}
                multiChoice={true}
                placeholder={i18n.t('chooseSubTemplates', {
                  ns: 'companyManagement',
                })}
                label={
                  i18n.t('subTemplates', { ns: 'companyManagement' }) as string
                }
              />
            </div>
            <div className="col s12 m6">
              <Select
                values={compulsoryFieldsOptions.filter((field) =>
                  this.state.compulsoryFields.includes(field.value.toString())
                )}
                options={compulsoryFieldsOptions}
                onChangeValues={this.handleChangeCompulsoryFields}
                multiChoice={true}
                placeholder={messages.compulsoryFields}
                label={messages.compulsoryFields}
              />
            </div>
          </div>
          <div className="row">
            <div className="col s12 m6">
              <label htmlFor="effectiveDateType">
                {
                  i18n.t('effectiveDateType', {
                    ns: 'companyManagement',
                  }) as string
                }
              </label>
              <select
                id="effectiveDateType"
                name="effectiveDateType"
                value={template.effectiveDateType}
                onChange={this.handleSelectChange}
                className="browser-default"
              >
                <option value="dueDate">
                  {i18n.t('dueDate', { ns: 'companyManagement' }) as string}
                </option>
                <option value="startDate">
                  {i18n.t('startDate', { ns: 'companyManagement' }) as string}
                </option>
                <option value="dueVerify">
                  {i18n.t('dueVerify', { ns: 'companyManagement' }) as string}
                </option>
                <option value="creationDate">
                  {
                    i18n.t('creationDate', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </option>
                <option value="parentStartDate">
                  {
                    i18n.t('parentStartDate', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </option>
                <option value="parentDueDate">
                  {
                    i18n.t('parentDueDate', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </option>
                <option value="parentDueVerify">
                  {
                    i18n.t('parentDueVerify', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </option>
                <option value="parentCreationDate">
                  {
                    i18n.t('parentCreationDate', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </option>
              </select>
            </div>
          </div>
          <Collapsible
            header={i18n.t('printTemplateConfig', { ns: 'companyManagement' })}
            nested={true}
            style={{ overflow: 'visible' }}
          >
            <div className="row"></div>
            <Select
              label={i18n.t('header', { ns: 'companyManagement' }) as string}
              options={availablePrintParts.filter((it) =>
                it.available.includes('head')
              )}
              values={printHeaderValues}
              onChangeValues={(printHeaderValues: IOption[]) => {
                this.setState({ printHeaderValues: printHeaderValues as any });
              }}
              multiChoice={true}
              containerStyle={{ overflow: 'visible' }}
            />
            <Select
              label={i18n.t('body', { ns: 'companyManagement' }) as string}
              options={availablePrintParts.filter((it) =>
                it.available.includes('body')
              )}
              values={printBodyValues}
              onChangeValues={(printBodyValues: IOption[]) => {
                this.setState({ printBodyValues: printBodyValues as any });
              }}
              multiChoice={true}
            />
            {printBodyValues.find((it) =>
              it.value.toString().startsWith('Subtasks')
            ) && (
              <Select
                label={
                  i18n.t('subTasks', { ns: 'companyManagement' }) as string
                }
                options={availablePrintParts.filter((it) =>
                  it.available.includes('subtask')
                )}
                values={printSubtasksValues}
                onChangeValues={(printSubtasksValues: IOption[]) => {
                  this.setState({
                    printSubtasksValues: printSubtasksValues as any,
                  });
                }}
                multiChoice={true}
              />
            )}
          </Collapsible>
          <TemplateFields
            fields={template.fields}
            onAddField={this.handleAddField}
            onRemoveField={this.handleRemoveField}
            onChangeField={this.handleChangeFieldName}
            onChangeFieldType={this.handleChangeFieldType}
            onChangeFieldAutoFillFunc={this.handleChangeFieldAutoFillFunc}
            onChangeFieldOptions={this.handleChangeFieldOptions}
            onChangeFieldConfigs={this.handleChangeFieldConfigs}
            onMoveItem={this.handleMoveField}
          />
          <Collapsible
            header={i18n.t('customTitles', { ns: 'companyManagement' })}
          >
            <div className="row">
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="title"
                  type="text"
                  name="title"
                  value={template.title || ''}
                  onChange={this.handleInputChange}
                  placeholder={i18n.t('defaultTitle', {
                    ns: 'companyManagement',
                  })}
                  label={
                    i18n.t('defaultTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="assigneeTitle"
                  type="text"
                  name="assigneeTitle"
                  value={template.assigneeTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={i18n.t('assigneeTitle', {
                    ns: 'companyManagement',
                  })}
                  label={
                    i18n.t('assigneeTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="followersTitle"
                  type="text"
                  name="followersTitle"
                  value={template.followersTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={i18n.t('followersTitle', {
                    ns: 'companyManagement',
                  })}
                  label={
                    i18n.t('followersTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="subTasksTitle"
                  type="text"
                  name="subTasksTitle"
                  value={template.subTasksTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={i18n.t('subTasksTitle', {
                    ns: 'companyManagement',
                  })}
                  label={
                    i18n.t('subTasksTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="chatTitle"
                  type="text"
                  name="chatTitle"
                  value={template.chatTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('chatTitle', { ns: 'companyManagement' }) as string
                  }
                  label={
                    i18n.t('chatTitle', { ns: 'companyManagement' }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="dueDateTitle"
                  type="text"
                  name="dueDateTitle"
                  value={template.dueDateTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('dueDateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('dueDateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="startDateTitle"
                  type="text"
                  name="startDateTitle"
                  value={template.startDateTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={i18n.t('startDateTitle', {
                    ns: 'companyManagement',
                  })}
                  label={
                    i18n.t('startDateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifyDateTitle"
                  type="text"
                  name="verifyDateTitle"
                  value={template.verifyDateTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifyDateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifyDateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifierTitle"
                  type="text"
                  name="verifierTitle"
                  value={template.verifierTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifierTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifierTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifyStateTitle"
                  type="text"
                  name="verifyStateTitle"
                  value={template.verifyStateTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifyStateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifyStateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="assigneeStateTitle"
                  type="text"
                  name="assigneeStateTitle"
                  value={template.assigneeStateTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('assigneeStateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('assigneeStateTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="assigneeStartedTitle"
                  type="text"
                  name="assigneeStartedTitle"
                  value={template.assigneeStartedTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('assigneeStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('assigneeStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifierRejectTitle"
                  type="text"
                  name="verifierRejectTitle"
                  value={template.verifierRejectTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifierRejectTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifierRejectTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="assigneeCompleteTitle"
                  type="text"
                  name="assigneeCompleteTitle"
                  value={template.assigneeCompleteTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('assigneeCompleteTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('assigneeCompleteTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifierStartedTitle"
                  type="text"
                  name="verifierStartedTitle"
                  value={template.verifierStartedTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifierStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifierStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="assigneeNotStartedTitle"
                  type="text"
                  name="assigneeNotStartedTitle"
                  value={template.assigneeNotStartedTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('assigneeNotStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('assigneeNotStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifierCompleteTitle"
                  type="text"
                  name="verifierCompleteTitle"
                  value={template.verifierCompleteTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifierCompleteTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifierCompleteTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="verifierNotStartedTitle"
                  type="text"
                  name="verifierNotStartedTitle"
                  value={template.verifierNotStartedTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('verifierNotStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('verifierNotStartedTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="relatedProviderItemTitle"
                  type="text"
                  name="relatedProviderItemTitle"
                  value={template.relatedProviderItemTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('relatedProviderItemTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('relatedProviderItemTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="undraftTaskTitle"
                  type="text"
                  name="undraftTaskTitle"
                  value={template.undraftTaskTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('undraftTaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('undraftTaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="undraftTaskAndSubtaskTitle"
                  type="text"
                  name="undraftTaskAndSubtaskTitle"
                  value={template.undraftTaskAndSubtaskTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('undraftTaskAndSubtaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('undraftTaskAndSubtaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="taskPublisherTitle"
                  type="text"
                  name="taskPublisherTitle"
                  value={template.taskPublisherTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('taskPublisherTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('taskPublisherTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="subTaskPublisherTitle"
                  type="text"
                  name="subTaskPublisherTitle"
                  value={template.subTaskPublisherTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('subTaskPublisherTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('subTaskPublisherTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="readyToPublishTaskTitle"
                  type="text"
                  name="readyToPublishTaskTitle"
                  value={template.readyToPublishTaskTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('readyToPublishTaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('readyToPublishTaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
              <div className="col s12 m6" style={{ float: floatDir }}>
                <Input
                  id="readyToPublishSubTaskTitle"
                  type="text"
                  name="readyToPublishSubTaskTitle"
                  value={template.readyToPublishSubTaskTitle || ''}
                  onChange={this.handleInputChange}
                  placeholder={
                    i18n.t('readyToPublishSubTaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  label={
                    i18n.t('readyToPublishSubTaskTitle', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </div>
            </div>
          </Collapsible>
          <TemplateReport
            templateReportOptions={
              template.templateReportOptions as ITemplateReportOptions
            }
            onChangeReportTitle={this.handleChangeReportOption}
            taskProgressType={template.taskProgressType}
            onChangeProgressType={this.handleChangeProgressType}
          />
          <TemplateNotifications
            onChangeInput={this.handleChangeNotificationProperties}
            handleChangeSelectNotificationProperties={
              this.handleChangeSelectNotificationProperties
            }
            notificationProperties={template.notificationProperties}
            template={template}
            onAddFieldNotification={this.handleAddFieldNotifications}
            onRemoveFieldNotification={this.handleRemoveFieldNotifications}
            onUpdateFieldNotification={
              this.handleUpdateFieldNotificationProperties
            }
            questionFields={this.state.template.fields}
            company={this.props.company}
          />
          <Collapsible
            header={i18n.t('remindersConfig', { ns: 'companyManagement' })}
          >
            <div className="row">
              <Button color="blue" onClick={this.handleAddReminder}>
                {i18n.t('addReminder', { ns: 'companyManagement' }) as string}
              </Button>
            </div>
            <div className="row">
              {template.reminderInfos.map((reminder, index) => {
                return (
                  <TaskReminder
                    key={index}
                    reminder={reminder}
                    reminderIndex={index}
                    assigneeTitle={template.assigneeTitle}
                    verifierTitle={template.verifierTitle}
                    removeReminder={this.removeReminder}
                    followersTitle={template.followersTitle}
                    onReminderChange={this.handleReminderInputChange}
                    template={template}
                  />
                );
              })}
            </div>
          </Collapsible>

          <div className="row" style={{ margin: '1rem 0' }}>
            <div className="col s12 m6">
              <Input
                type="checkbox"
                id="suggestOnDocumentManager"
                name="suggestOnDocumentManager"
                checked={this.state.suggestOnDocumentManager}
                onChange={this.handleInputChange}
                label={
                  i18n.t('suggestOnDocumentManager', {
                    ns: 'companyManagement',
                  }) as string
                }
              />
            </div>
            {
              // editing mode
              this.props.template && (
                <div className="col s12 m6">
                  <Input
                    id="applyTemplate"
                    type="checkbox"
                    name="applyTemplate"
                    label={
                      i18n.t('applyTemplateToPastTasks', {
                        ns: 'companyManagement',
                      }) as string
                    }
                    checked={applyTemplate}
                    onChange={this.handleInputChange}
                  />
                </div>
              )
            }
          </div>
          <SpaceBetween style={{ margin: '2em 0' }}>
            <Button color="green" type="submit" loading={saving}>
              {i18n.t('save', { ns: 'companyManagement' }) as string}
            </Button>
            <Button color="red" onClick={this.handleCancel}>
              {i18n.t('cancel', { ns: 'companyManagement' }) as string}
            </Button>
          </SpaceBetween>
        </form>
      </React.Fragment>
    );
  }

  private getSelectedAssigneeAsOption(
    verifier: null | { [key: number]: IAssignmentView },
    users: IUsers,
    company: ICompany
  ) {
    return (
      Object.values(verifier || {})?.map((it) => {
        return this.getSelectedVerifierAsOption(it, users, company)[0];
      }) ?? []
    );
  }

  private getSelectedVerifiersAsOption(
    verifiers: null | { [key: number]: IAssignmentView },
    users: IUsers,
    company: ICompany
  ) {
    return (
      Object.values(verifiers || {})?.map((it) => {
        return this.getSelectedVerifierAsOption(it, users, company)[0];
      }) ?? []
    );
  }

  private getSelectedVerifierAsOption(
    verifier: null | IAssignmentView,
    users: IUsers,
    company: ICompany
  ) {
    let selectedVerifier = [] as IOption[];
    if (verifier?.userId)
      selectedVerifier = [
        {
          value: verifier.userId,
          label: users.map[verifier.userId]?.name || '',
        },
      ];
    if (verifier?.properties?.roleId)
      selectedVerifier = [
        {
          value: -verifier?.properties?.roleId,
          label: company.roles[verifier?.properties?.roleId]?.displayName || '',
        },
      ];
    if (verifier?.properties?.projectRoleId)
      selectedVerifier = [
        {
          value: -verifier?.properties?.projectRoleId,
          label:
            company.roles[verifier?.properties?.projectRoleId]?.displayName ||
            '',
        },
      ];
    return selectedVerifier;
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const templateId = Number(props.templateId);
  const companyId = Number(props.companyId);
  const template = state.tasks.data[templateId];
  const company = state.companies.data[companyId];
  const tasks = state.tasks;
  const thisUser = state.thisUser;
  const users = state.users;

  return { template, company, tasks, thisUser, users };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  addTemplate: (template: ICompanyTemplate) =>
    appDispatch(addTemplate({ companyTemplate: template })),
  updateTemplate: (template: ICompanyTemplate) =>
    appDispatch(updateTemplate({ companyTemplate: template })),
  requireSeveralUser: (usersList: number[]) =>
    appDispatch(requireSeveralUser({ usersList })),
  applyTemplateToPastTasks: (templateId: number) =>
    appDispatch(applyTemplateToPastTasks({ templateId })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompanyTemplateEditor);
